/* eslint-disable no-unused-vars */
/** @module Auth0PostLoginHandlerBase */
/* eslint-disable import/extensions */
import Auth0UtilsBrowser from '../../../utils/auth0-utils-browser.mjs';
import { resolveSsasBaseUrl } from '../../../utils/ssas-urls.mjs';
import Auth0Constants from '../../../utils/auth0-constants.mjs';
import Auth0CookieHelper from '../../../utils/auth0-cookie-helper.mjs';
import Auth0LocalStorageHelper from '../../../utils/auth0-local-storage-helper.mjs';
import Auth0TokenStorageHelper from './auth0-token-storage-helper.mjs';
import Auth0WhiteLabelAssetHelper from '../auth0-white-label-helper.mst.mjs';
import Auth0ManagementApiBrowser from '../../../utils/auth0-management-api-browser.mjs';
import Auth0SupportedIdps from '../../../utils/auth0-supported-idps.mjs';
import Auth0WaitSpinner from '../../pages/auth0-wait-spinner-page-helper.mst.mjs';
import logger from '../../../utils/logging/auth0-logger.mjs';

/**
 * @abstract
 */
class Auth0PostLoginHandlerBase {
  constructor(options) {
    this._name = options.name;
    this._description = options.description;
    this._isDefault = options.isDefault ?? false;
    this._ssOptions = options.ssOptions;
    const resolved = Auth0UtilsBrowser.tenantOrDomainResolver(options.tenantOrDomain);
    this._tenant = resolved.tenant;
    this._idpContext = options.idpContext;
    this._whiteLabelOptions = options.whiteLabelOptions || {};
    this._captchaOptions = options.captchaOptions;

    this._whiteLabelHelper = new Auth0WhiteLabelAssetHelper({
      tenantOrDomain: this._tenant.id,
      ...this._whiteLabelOptions,
    });

    this._appLoginUrl = this._ssOptions?.redirectAuth0Uri || Auth0UtilsBrowser.locationWithNoSearchParams;
    this._clientId = options.clientId;
    this._providerType = options.providerType;
    this._tokenStorageConfig = options.tokenStorageConfig ?? null;
    // declared in the handler's registry entry: does this handler redirect the user off
    // the login domain as its final action? (see auth0-post-login-handler-config.mjs)
    this._crossDomainRedirect = options.crossDomainRedirect ?? false;
  }

  get name() {
    return this._name;
  }

  get crossDomainRedirect() {
    return this._crossDomainRedirect;
  }

  get description() {
    return this._description;
  }

  get isDefault() {
    return this._isDefault;
  }

  /**
   * @function
   * @description This function determines, based on user and app state whether or not this handler is to be chosen as the post login handler. Only one will be chose (first one to return true here)
   * @param {object} loginData Data about the just logged in user
   * @param {object} loginData.user The auth0 user that logged in
   * @param {object} loginData.appState The app state prior to loginWithRedirect()
   * @param {object} [loginData.idpData] If SSO was used, this will hold an Auth0IdentityProvider representing the provider used for login
   * @param {string} [loginData.appState.redirectOverride] The URL to use for post-auth redirect (instead of going to SSHome)
   * @returns {bool} Indicating whether or not this handler should run
   */
  async shouldHandle(loginData) {
    throw new Error('Auth0PostLoginHandlerBase is an abstract class. You need to extend it and implement this function there...');
  }

  /**
   * @function
   * @description The Run() function holds the entirety of the post login logic for this handler. No other handler will run and this function is responsible for the final redirect action
   * @param {object} loginData Data about the just logged in user
   * @param {object} loginData.user The auth0 user that logged in
   * @param {object} loginData.appState The app state prior to loginWithRedirect()
   * @param {object} [loginData.idpData] If SSO was used, this will hold an Auth0IdentityProvider representing the provider used for login
   * @param {string} [loginData.appState.redirectOverride] The URL to use for post-auth redirect (instead of going to SSHome)
   */
  async run(loginData) {
    throw new Error('Auth0PostLoginHandlerBase is an abstract class. You need to extend it and implement this function there...');
  }

  get _socialIdpHelper() {
    if (this.__socialIdpHelper) {
      return this.__socialIdpHelper;
    }
    this.__socialIdpHelper = new Auth0SupportedIdps({
      tenantOrDomain: this._tenant.id,
      appLoginHandlerUrl: this._ssOptions.redirectAuth0Uri,
    });
    return this.__socialIdpHelper;
  }

  get _auth0ManagementApi() {
    if (!this._a0Api) {
      this._a0Api = new Auth0ManagementApiBrowser({
        tenantOrDomain: this._tenant.id,
        clientId: this._ssOptions.clientId,
        authServiceBaseUrl: resolveSsasBaseUrl('auth-stage', 'shipstation.com,sslocal.com', window.location.hostname),
      });
    }
    return this._a0Api;
  }

  _initLogger = (sourceName) => {
    logger.initialize({
      sumoEndpoint: 'https://endpoint2.collection.us2.sumologic.com/receiver/v1/http/ZaVnC4dhaV1uAI96duDvnKsBH-5zBCQS4Z9yRC6qyLXrEdfezmbhON_4so37Qor_6qM1eto00rRWjhlaqRccMgcVRvrLAKRfcyNBdeziv7B_grZ3H8sjew==',
      sourceName,
    });
  };

  _handledUnexpectedException = async (e) => {
    logger.error('Unexpected error in Auth0PostLoginHandlerBase', e);
    await logger.log(e);
    if (this.sentry) {
      this.sentry.captureException(e);
    }
  };

  /**
   * @function
   * @description Cross-subdomain logout safety cleanup. Applied automatically by
   *              Auth0PostLoginHandlerHelper after a handler registered with
   *              `crossDomainRedirect: true` completes its run().
   *
   *              When a user logs in on one subdomain (e.g. ship2) and is redirected to
   *              their app on another (e.g. ship11), the app's logout can only clear state
   *              visible to its own subdomain. Without this cleanup, tokens left behind on
   *              the login subdomain keep it "authenticated" after the user has logged out
   *              at the destination, so a revisit of the login subdomain skips login.
   *
   *              - promotes the auth indicator and this login's token cookies to the TLD
   *                so any subdomain's logout can clear them
   *              - deletes client-scoped auth indicator cookies (they must not survive
   *                the handoff to another app's subdomain)
   *              - clears the auth0-spa-js token cache from this login origin's
   *                localStorage so the login subdomain no longer holds valid tokens
   */
  _applyCrossSubdomainLogoutSafety = () => {
    // token cookie names this login stored (defaults to the legacy SS _lat/_latv3 pair)
    const tokenCookieNames = (this._tokenStorageConfig || Auth0TokenStorageHelper.DEFAULT_TOKEN_STORAGE_CONFIG)
      .flatMap(config => [config.accessTokenName, config.refreshTokenName, config.idTokenName])
      .filter(Boolean);

    // cookies that travel: visible to (and therefore deletable by) the destination app's subdomain
    Auth0CookieHelper.promoteCookiesToTLD([
      Auth0Constants.AUTH0_IS_AUTHENTICATED,
      ...tokenCookieNames,
    ]);

    // remove client-scoped cookies that should not travel
    if (this._clientId) {
      Auth0CookieHelper.deleteCookies([
        Auth0Constants.AUTH0_IS_AUTHENTICATED_CLIENT(this._clientId),
        Auth0Constants.AUTH0_IS_AUTHENTICATED_LEGACY_CLIENT(this._clientId),
      ]);
    }

    Auth0LocalStorageHelper.resetAuth0State();
  };

  async _showSpinner(options = {}) {
    await new Auth0WaitSpinner({
      name: options.parent || 'Auth0PostLoginHandlerBase',
      waitTextOverrideKey: options.waitTextOverrideKey,
      waitTextReplacementArgs: options.waitTextReplacementArgs,
      tenantOrDomain: this._tenant.id,
      whiteLabelOptions: this._whiteLabelOptions,
    }).render();
  }
}

export default Auth0PostLoginHandlerBase;
